/**
 * THE cross-service vocabulary — every enum that `apps/web` and
 * `services/document-processing` must agree on, declared exactly once.
 *
 * ## Why this file exists
 *
 * docproc is a separate deployable in the same pnpm workspace, with no dependency on `web`, so it
 * cannot import from here at runtime. Its copy of these lists is therefore **generated** from this
 * file into `services/document-processing/src/lib/crossService.generated.ts` by
 * `apps/web/scripts/generate-shared-vocabulary.ts` (`pnpm --filter web vocabulary:generate`).
 * `tests/unit/crossServiceVocabulary.test.ts` regenerates in memory and fails if the checked-in copy is
 * stale, so drift is a red test with an exact fix command rather than a silent divergence.
 *
 * The generator copies this file **verbatim** below a DO-NOT-EDIT header. That is the whole reason
 * for the constraints below — keep them or the generated file stops being valid docproc TypeScript:
 *
 *   1. **No imports.** Nothing here may reference anything outside this file.
 *   2. **No web-only concerns.** No Nuxt/Vue/i18n/DB types, no `~`/`~~` aliases.
 *   3. **Plain declarations only** — `as const` arrays, types derived from them, and const objects.
 *      No runtime dependencies, no environment access.
 *
 * ## Why the enums must match, concretely
 *
 * These lists are what the extractor MODEL is shown as a JSON-Schema `enum`, and simultaneously what
 * the app validates authored data against. A member present on one side and not the other means the
 * model is taught a vocabulary the funnel rejects — the value arrives, fails the guard, and is
 * dropped silently. That is the failure mode this file removes: a template slot asking for a
 * `national_id` and a user-uploaded document tagged `national_id` are matchable only because both
 * spellings come from the same array.
 *
 * Anything that only ONE service needs does NOT belong here. Keep it in that service's own module.
 */

// ---------------------------------------------------------------------------------------------
// Entities — who, and what identifier (MAR-129)
// ---------------------------------------------------------------------------------------------

/** Who — the sort of actor an entity is. */
export const ENTITY_KINDS = ['person', 'organization', 'property', 'vehicle'] as const
export type EntityKind = (typeof ENTITY_KINDS)[number]

/** The subset of `ENTITY_KINDS` a contract party can be. Property and vehicles are entities, never
 *  parties. */
export const PARTY_KINDS = ['person', 'organization'] as const satisfies readonly EntityKind[]
export type PartyKind = (typeof PARTY_KINDS)[number]

/**
 * What — the sort of identifier, country-neutral.
 *
 * - `national_id` — state identity document for a person (DUI·SV, CUI·GT, Cédula·CR/PA/DO, DNI·AR/ES)
 * - `passport` — travel document
 * - `foreigner_id` — residence permit / foreigner registration
 * - `tax_id` — tax identifier, person **or** company (NIT·SV/GT/CO, VAT·EU, RTN·HN, RFC·MX, EIN·US)
 * - `business_registration` — commercial-registry number (HRB·DE, KBO/BCE·BE, Companies House·GB)
 * - `other` — a real identifier no category fits
 *
 * Never fuse the country into the type: "DUI" is a `national_id` issued by `SV`, and the two facts
 * live on separate axes. A null `id_type` means no identifier is known — that is not a member.
 */
export const ENTITY_ID_TYPES = ['national_id', 'passport', 'foreigner_id', 'tax_id', 'business_registration', 'other'] as const
export type EntityIdType = (typeof ENTITY_ID_TYPES)[number]

// ---------------------------------------------------------------------------------------------
// Document classification — `items.contract_type`
// ---------------------------------------------------------------------------------------------

/**
 * Contract/document categories detected from real ingested document TEXT (the entity extractor).
 * `poder` = notarial power of attorney (poder / mandato); `other` stays the catch-all.
 */
export const INGESTED_CONTRACT_TYPES = ['employment', 'rental', 'services', 'poder', 'other'] as const
export type IngestedContractType = (typeof INGESTED_CONTRACT_TYPES)[number]

/**
 * Coarse classes from the IMAGE classifier (vision model on photographed uploads).
 *
 * **`identity` is ONE class, deliberately.** It used to be three — `sv-dui-front`, `sv-dui-back`,
 * `id-other` — which fused three orthogonal facts into a classification: *what* the document is
 * (an identity document), *where* it was issued (El Salvador), and *which side* was photographed.
 * That is the same fusion MAR-129 removed from the entity axes, and it failed the same way: the
 * live path assigned `sv-dui-back` on MRZ-success-and-SLV, so a passport with a perfectly readable
 * MRZ was filed as "some other ID". The issuer is already in the MRZ (`issuingState`) and the
 * category is already in the document code, so the class carries neither.
 *
 * "Which side" is not a class either — it is whether identity data could be read at all, which is
 * `identityFrom(extracted_metadata) === null` (`shared/entities/identityDocument.ts`). A front, an
 * unreadable photo and a glare-ruined scan are all `identity` documents that yielded no fields.
 */
export const IMAGE_DOCUMENT_CLASSES = [
  'identity', 'contract-scan', 'receipt', 'photo', 'other',
] as const
export type ImageDocumentClass = (typeof IMAGE_DOCUMENT_CLASSES)[number]

// ---------------------------------------------------------------------------------------------
// Dates — `item_dates.date_kind` (MAR-996)
// ---------------------------------------------------------------------------------------------

/**
 * The canonical KIND of an extracted date — a closed set, set at write time.
 *
 * `item_dates.date_type` stays beside it as the label in the document's own words ("vencimiento",
 * "fin del arrendamiento", "fecha de emisión"): free text, shown to the user, never filtered on. It
 * was filtered on, with ILIKE, and a model reasoning in English asked for `'expiry'` — which no
 * Spanish label contains (DOCS-2.03). Search now filters on this kind only.
 *
 * Chosen from the labels dev actually held on 2026-10-01 (firma/inicio/fin/vencimiento/birth_date/
 * expiration_date were 90 % of the rows) and the synonym sets `vigenciaExtraction.ts` kept by hand.
 * Two choices are deliberate:
 *
 * - **`expiry` covers both "end" and "expiry".** "fin", "terminación" and "vencimiento" are how one
 *   document or another says the same thing, and the vigencia reader already treated them as one.
 *   Splitting them makes the model pick between two kinds for "contracts ending in 2026" and lose
 *   the documents filed under the other.
 * - **`grant` is not `signature`.** A credencial carries the date the junta ELECTED the officer and
 *   the date the notary SIGNED; the vigencia reader needs the first, and keeping them apart is what
 *   lets it prefer it.
 *
 * `other` is the escape hatch, so a valid answer always exists. Add a member only with a backfill
 * for the rows it would have claimed: a kind nobody wrote is a filter that matches nothing.
 */
export const DATE_KINDS = [
  'signature', 'grant', 'start', 'expiry', 'renewal', 'issue', 'registration', 'birth', 'deadline', 'other',
] as const
export type DateKind = (typeof DATE_KINDS)[number]

/**
 * What each `DATE_KINDS` member means — shown to the extractor model (as the field description) and
 * to the chat model (as the filter enum's description). English per services/agent-web-chat/AGENTS.md; the Spanish
 * phrasings are the DATA each kind is recognised by, quoted rather than translated.
 */
export const DATE_KIND_DESCRIPTION = `The canonical KIND of a date. One of:
- "signature" — when the document was signed or executed ("firma", "suscripción", "celebración")
- "grant" — when a power, an appointment or an authorization was conferred ("otorgamiento", "nombramiento", "elección", "designación", "acuerdo", "autorización")
- "start" — when it takes effect or a term begins ("inicio", "vigencia desde", "fecha efectiva", "entrada en vigor")
- "expiry" — when it ends, expires or terminates; end and expiry are ONE kind ("vencimiento", "fin", "terminación", "finalización", "caducidad", "vigencia hasta", an ID card's expiration date)
- "renewal" — a renewal or extension ("renovación", "prórroga")
- "issue" — when a document, certificate or title was issued or created ("emisión", "expedición", "creación")
- "registration" — incorporation of a company or inscription in a public registry ("constitución", "inscripción", "registro")
- "birth" — a person's date of birth ("nacimiento")
- "deadline" — a due date for an obligation: a payment, a delivery, a filing ("pago", "entrega", "plazo", "fecha límite")
- "other" — any other date`

// ---------------------------------------------------------------------------------------------
// Party & entity attributes — the EAV key vocabulary (MAR-176, MAR-1068)
// ---------------------------------------------------------------------------------------------

/**
 * Canonical, CLOSED set of person/party-attribute KEYS (English, snake_case) — the single source of
 * truth for what an attribute a person party carries can be called.
 *
 * Pinning the key set is what prevents the EAV catalog from fragmenting: two ingestions of the same
 * kind of document always yield the SAME keys, so a query on `marital_status` cannot miss because
 * one import wrote `estado_familiar` and another `estadoCivil`.
 *
 * Only the KEYS are enumerated. VALUES stay free text — the model transcribes whatever is printed
 * (`SOLTERO(A)`, `M`/`F`/`X`, `SAN MIGUEL`, …), because value domains vary by country (sex can be
 * unspecified/other; marital-status wording differs). Keys are English per the repo rule
 * (identifiers in English, source-language content in values).
 *
 * `given_names` and `surnames` are separate facts from the composed `entities.name` (MAR-1068): a
 * template that asks `[NOMBRES_TRABAJADOR]` and `[APELLIDOS_TRABAJADOR]` separately fills from them
 * when a source (such as an MRZ or explicit brief attribute) stated them separately, and never
 * splits a composed name by word-counting heuristics.
 *
 * This is also the person subset a run-form input may bind to via `entitySource.field:
 * 'attribute:<key>'` — `shared/entities/attributeKeys.ts` derives its canonical list from this one
 * rather than declaring a second, so a fact the extractor can state and a fact an input can request
 * are the same set by construction. They were briefly two lists (MAR-176), which is how `company`
 * came to be requestable by the run form but unnameable by the extractor.
 */
export const PARTY_ATTRIBUTE_KEYS = [
  'given_names', 'surnames', 'sex', 'nationality', 'birth_date',
  'address', 'municipality', 'department', 'place_of_birth', 'occupation', 'marital_status',
  'company', 'id_issue_place', 'id_issue_date',
] as const
export type PartyAttributeKey = (typeof PARTY_ATTRIBUTE_KEYS)[number]

/** The subset of keys extracted from the printed zones of an identity card (the rest come off the
 *  MRZ). `company` is not among them — an ID card does not state an employer; that fact reaches a
 *  party from a parsed contract or from the linked principal organization. */
export const PRINTED_FIELD_KEYS = [
  'address', 'municipality', 'department', 'place_of_birth', 'occupation', 'marital_status',
  'id_issue_place', 'id_issue_date',
] as const
export type PrintedFieldKey = (typeof PRINTED_FIELD_KEYS)[number]

/** Spanish display labels (UI text only — the key is the stable English identifier). */
export const PARTY_ATTRIBUTE_LABELS: Record<PartyAttributeKey, string> = {
  given_names: 'Nombres',
  surnames: 'Apellidos',
  sex: 'Sexo',
  nationality: 'Nacionalidad',
  birth_date: 'Fecha de nacimiento',
  address: 'Dirección',
  municipality: 'Municipio',
  department: 'Departamento',
  place_of_birth: 'Lugar de nacimiento',
  occupation: 'Profesión u oficio',
  marital_status: 'Estado familiar',
  company: 'Empresa',
  id_issue_place: 'Lugar de expedición del documento',
  id_issue_date: 'Fecha de expedición del documento',
}

/**
 * Closed set of EAV attribute keys across ALL four `ENTITY_KINDS` (`person`, `organization`,
 * `property`, `vehicle` — MAR-1068). Supersets `PARTY_ATTRIBUTE_KEYS` with the organization,
 * property and vehicle attributes a template can ask about.
 */
export const ENTITY_ATTRIBUTE_KEYS = [
  'given_names', 'surnames', 'sex', 'nationality', 'birth_date',
  'address', 'municipality', 'department', 'place_of_birth', 'occupation', 'marital_status',
  'company', 'id_issue_place', 'id_issue_date',
  'tax_id', 'registration_number', 'passport', 'foreigner_id', 'national_id',
  'legal_form', 'country', 'economic_activity', 'representative',
  'registry_number', 'cadastral_number', 'area',
  'plate', 'vin', 'engine_number', 'make', 'model', 'year', 'color',
] as const
export type EntityAttributeKey = (typeof ENTITY_ATTRIBUTE_KEYS)[number]

/** Spanish display labels for every `EntityAttributeKey` across all four entity kinds. */
export const ENTITY_ATTRIBUTE_LABELS: Record<EntityAttributeKey, string> = {
  ...PARTY_ATTRIBUTE_LABELS,
  tax_id: 'NIT / Identificación tributaria',
  registration_number: 'Matrícula de comercio',
  passport: 'Pasaporte',
  foreigner_id: 'Carné de residente',
  national_id: 'Documento de identidad',
  legal_form: 'Forma jurídica',
  country: 'País',
  economic_activity: 'Giro o actividad económica',
  representative: 'Representante legal',
  registry_number: 'Matrícula (CNR)',
  cadastral_number: 'Número catastral',
  area: 'Área',
  plate: 'Placa',
  vin: 'Número de chasis (VIN)',
  engine_number: 'Número de motor',
  make: 'Marca',
  model: 'Modelo',
  year: 'Año',
  color: 'Color',
}

// ---------------------------------------------------------------------------------------------
// Entity fact vocabulary per kind — what a template can ask about each kind (MAR-1068, MAR-1104)
// ---------------------------------------------------------------------------------------------

/**
 * One fact a template slot of a given `EntityKind` can ask for.
 *
 * - `key`: canonical flat property key within the entity kind (`name`, `given_names`, `national_id`, `age`, etc.).
 * - `field`: flat property key (`field === key`) understood by `valueForTarget` / `rawValueForFact`.
 * - `label`: Spanish display label shown to the template author and in the run form.
 */
export interface EntityFact {
  readonly key: string
  readonly field: string
  readonly label: string
}

/**
 * Flat identifier properties across `person` and `organization` slots (MAR-1104).
 * Specific identifier properties imply an `EntityIdType` via `idTypeForProperty`; `id_number` is generic.
 */
export const IDENTIFIER_PROPERTIES = [
  'national_id',
  'tax_id',
  'passport',
  'resident_card',
  'foreigner_id',
  'business_registration',
  'id_number',
] as const
export type IdentifierProperty = (typeof IDENTIFIER_PROPERTIES)[number]

const SPECIFIC_ID_TYPE_BY_PROPERTY: Record<string, EntityIdType> = {
  national_id: 'national_id',
  tax_id: 'tax_id',
  passport: 'passport',
  resident_card: 'foreigner_id',
  foreigner_id: 'foreigner_id',
  business_registration: 'business_registration',
}

function normalizePropertyToken(prop: string | null | undefined): string {
  if (!prop) return ''
  let p = prop.trim()
  if (!p) return ''
  const dotIdx = p.lastIndexOf('.')
  if (dotIdx !== -1) p = p.slice(dotIdx + 1).trim()
  if (p.startsWith('attribute:')) p = p.slice('attribute:'.length).trim()
  else if (p.startsWith('derived:')) p = p.slice('derived:'.length).trim()
  return p
}

function isIdTypeLiteral(v: string): v is EntityIdType {
  return (ENTITY_ID_TYPES as readonly string[]).includes(v)
}

/**
 * True when `prop` names an entity identifier property (`national_id`, `tax_id`, `passport`,
 * `resident_card`, `foreigner_id`, `business_registration`, `id_number`), a `<slot>.<property>`
 * binding to one, or legacy `idNumber` / `idNumber:<idType>`.
 */
export function isIdentifierProperty(prop: string | null | undefined): boolean {
  const token = normalizePropertyToken(prop)
  if (!token) return false
  if (token === 'idNumber') return true
  if (token.startsWith('idNumber:')) {
    const suffix = token.slice('idNumber:'.length).trim()
    return (IDENTIFIER_PROPERTIES as readonly string[]).includes(suffix) || isIdTypeLiteral(suffix)
  }
  return (IDENTIFIER_PROPERTIES as readonly string[]).includes(token)
}

/**
 * Return the canonical `EntityIdType` implied by a specific identifier property (`national_id` →
 * `'national_id'`, `tax_id` → `'tax_id'`, `passport` → `'passport'`, `resident_card` /
 * `foreigner_id` → `'foreigner_id'`, `business_registration` → `'business_registration'`), or
 * `null` for generic `id_number` / `idNumber` and non-identifier properties.
 */
export function idTypeForProperty(prop: string | null | undefined): EntityIdType | null {
  const token = normalizePropertyToken(prop)
  if (!token) return null
  if (token.startsWith('idNumber:')) {
    const suffix = token.slice('idNumber:'.length).trim()
    return SPECIFIC_ID_TYPE_BY_PROPERTY[suffix] ?? (isIdTypeLiteral(suffix) ? suffix : null)
  }
  return SPECIFIC_ID_TYPE_BY_PROPERTY[token] ?? null
}

/** Facts a template can ask about a `person` (natural person). */
export const PERSON_FACTS = [
  { key: 'name', field: 'name', label: 'Nombre completo' },
  { key: 'given_names', field: 'given_names', label: PARTY_ATTRIBUTE_LABELS.given_names },
  { key: 'surnames', field: 'surnames', label: PARTY_ATTRIBUTE_LABELS.surnames },
  { key: 'national_id', field: 'national_id', label: 'DUI / Documento nacional de identidad' },
  { key: 'tax_id', field: 'tax_id', label: ENTITY_ATTRIBUTE_LABELS.tax_id },
  { key: 'passport', field: 'passport', label: 'Pasaporte' },
  { key: 'resident_card', field: 'resident_card', label: 'Carné de residente' },
  { key: 'foreigner_id', field: 'foreigner_id', label: 'Carné de extranjero / residente' },
  { key: 'id_number', field: 'id_number', label: 'Número de documento' },
  { key: 'sex', field: 'sex', label: PARTY_ATTRIBUTE_LABELS.sex },
  { key: 'nationality', field: 'nationality', label: PARTY_ATTRIBUTE_LABELS.nationality },
  { key: 'birth_date', field: 'birth_date', label: PARTY_ATTRIBUTE_LABELS.birth_date },
  { key: 'address', field: 'address', label: PARTY_ATTRIBUTE_LABELS.address },
  { key: 'municipality', field: 'municipality', label: PARTY_ATTRIBUTE_LABELS.municipality },
  { key: 'department', field: 'department', label: PARTY_ATTRIBUTE_LABELS.department },
  { key: 'place_of_birth', field: 'place_of_birth', label: PARTY_ATTRIBUTE_LABELS.place_of_birth },
  { key: 'occupation', field: 'occupation', label: PARTY_ATTRIBUTE_LABELS.occupation },
  { key: 'marital_status', field: 'marital_status', label: PARTY_ATTRIBUTE_LABELS.marital_status },
  { key: 'company', field: 'company', label: PARTY_ATTRIBUTE_LABELS.company },
  { key: 'id_issue_place', field: 'id_issue_place', label: PARTY_ATTRIBUTE_LABELS.id_issue_place },
  { key: 'id_issue_date', field: 'id_issue_date', label: PARTY_ATTRIBUTE_LABELS.id_issue_date },
  { key: 'age', field: 'age', label: 'Edad (calculada)' },
] as const satisfies readonly EntityFact[]

/** Facts a template can ask about an `organization` (company / legal entity). */
export const ORGANIZATION_FACTS = [
  { key: 'name', field: 'name', label: 'Nombre o razón social' },
  { key: 'tax_id', field: 'tax_id', label: ENTITY_ATTRIBUTE_LABELS.tax_id },
  { key: 'business_registration', field: 'business_registration', label: 'NRC / Registro fiscal o mercantil' },
  { key: 'registration_number', field: 'registration_number', label: ENTITY_ATTRIBUTE_LABELS.registration_number },
  { key: 'id_number', field: 'id_number', label: 'Número de documento' },
  { key: 'legal_form', field: 'legal_form', label: ENTITY_ATTRIBUTE_LABELS.legal_form },
  { key: 'address', field: 'address', label: ENTITY_ATTRIBUTE_LABELS.address },
  { key: 'municipality', field: 'municipality', label: ENTITY_ATTRIBUTE_LABELS.municipality },
  { key: 'department', field: 'department', label: ENTITY_ATTRIBUTE_LABELS.department },
  { key: 'nationality', field: 'nationality', label: ENTITY_ATTRIBUTE_LABELS.nationality },
  { key: 'country', field: 'country', label: ENTITY_ATTRIBUTE_LABELS.country },
  { key: 'economic_activity', field: 'economic_activity', label: ENTITY_ATTRIBUTE_LABELS.economic_activity },
  { key: 'representative', field: 'representative', label: ENTITY_ATTRIBUTE_LABELS.representative },
] as const satisfies readonly EntityFact[]

/**
 * Facts a Salvadoran (`sv`) template can ask about a `property` (inmueble):
 * CNR matrícula, cadastral number, location/address, municipality, and area.
 */
export const SV_PROPERTY_FACTS = [
  { key: 'registry_number', field: 'registry_number', label: ENTITY_ATTRIBUTE_LABELS.registry_number },
  { key: 'cadastral_number', field: 'cadastral_number', label: ENTITY_ATTRIBUTE_LABELS.cadastral_number },
  { key: 'address', field: 'address', label: 'Ubicación / dirección' },
  { key: 'municipality', field: 'municipality', label: ENTITY_ATTRIBUTE_LABELS.municipality },
  { key: 'area', field: 'area', label: ENTITY_ATTRIBUTE_LABELS.area },
] as const satisfies readonly EntityFact[]

/**
 * Facts a Salvadoran (`sv`) template can ask about a `vehicle` (vehículo):
 * plate, chassis/VIN, engine number, make, model, year, and color.
 */
export const SV_VEHICLE_FACTS = [
  { key: 'plate', field: 'plate', label: ENTITY_ATTRIBUTE_LABELS.plate },
  { key: 'vin', field: 'vin', label: ENTITY_ATTRIBUTE_LABELS.vin },
  { key: 'engine_number', field: 'engine_number', label: ENTITY_ATTRIBUTE_LABELS.engine_number },
  { key: 'make', field: 'make', label: ENTITY_ATTRIBUTE_LABELS.make },
  { key: 'model', field: 'model', label: ENTITY_ATTRIBUTE_LABELS.model },
  { key: 'year', field: 'year', label: ENTITY_ATTRIBUTE_LABELS.year },
  { key: 'color', field: 'color', label: ENTITY_ATTRIBUTE_LABELS.color },
] as const satisfies readonly EntityFact[]

export const PROPERTY_FACTS = SV_PROPERTY_FACTS
export const VEHICLE_FACTS = SV_VEHICLE_FACTS

/** Per-kind fact lists (using the `sv` registry sets for `property` and `vehicle`). */
export const ENTITY_FACTS: Record<EntityKind, readonly EntityFact[]> = {
  person: PERSON_FACTS,
  organization: ORGANIZATION_FACTS,
  property: PROPERTY_FACTS,
  vehicle: VEHICLE_FACTS,
}

export const ENTITY_FACTS_BY_KIND = ENTITY_FACTS

/**
 * Jurisdiction-keyed registry for kinds whose legal facts depend on a country's public registries
 * (`property` and `vehicle`). Adding a jurisdiction's property or vehicle vocabulary is a data
 * addition here — no lookup or UI code changes.
 */
export const ENTITY_FACTS_BY_JURISDICTION: Record<string, Partial<Record<EntityKind, readonly EntityFact[]>>> = {
  sv: {
    property: SV_PROPERTY_FACTS,
    vehicle: SV_VEHICLE_FACTS,
  },
}

/**
 * Return the fact vocabulary a template of `jurisdiction` may ask of an entity of `kind`.
 *
 * `person` and `organization` facts are country-neutral unless overridden in
 * `ENTITY_FACTS_BY_JURISDICTION`. `property` and `vehicle` facts are strictly jurisdiction-scoped:
 * a template whose jurisdiction has no registered entry for that kind gets `[]` (never another
 * country's registry vocabulary).
 */
export function entityFactsForKind(
  kind: EntityKind,
  jurisdiction?: string | null,
): readonly EntityFact[] {
  const normalized = (jurisdiction ?? '').trim().toLowerCase()
  const scoped = normalized ? ENTITY_FACTS_BY_JURISDICTION[normalized]?.[kind] : undefined
  if (scoped) return scoped
  if (kind === 'person' || kind === 'organization') return ENTITY_FACTS[kind]
  return []
}

const NON_ATTRIBUTE_FACT_KEYS = new Set<string>([
  'name',
  'idNumber',
  'id_number',
  'national_id',
  'passport',
  'resident_card',
  'foreigner_id',
  'business_registration',
  'age',
])

/**
 * The attribute-backed subset of `entityFactsForKind` (excludes top-level `name`, primary
 * identifier properties, and `age` / `derived:*` facts), for callers that iterate over EAV
 * attribute keys per kind.
 */
export function entityAttributeFactsForKind(
  kind: EntityKind,
  jurisdiction?: string | null,
): readonly EntityFact[] {
  return entityFactsForKind(kind, jurisdiction).filter(
    f => !NON_ATTRIBUTE_FACT_KEYS.has(f.key) && !f.field.startsWith('derived:'),
  )
}

// ---------------------------------------------------------------------------------------------
// Entity-merge evidence — what the merge judge may cite (MAR-10)
// ---------------------------------------------------------------------------------------------

/**
 * The facts the entity-merge judge may cite for or against two same-named mentions being one actor.
 * The model is shown this list as a JSON-Schema `enum`, and `web` drops anything outside it on every
 * read. Each fact is grounded in something the entity model already knows:
 *
 * - `tax_id` — `ENTITY_ID_TYPES.tax_id` (NIT, NRC, RUC, RFC, VAT, EIN), person or organization
 * - `personal_id` — a person's `national_id` / `passport` / `foreigner_id` (DUI, DPI/CUI, cédula…)
 * - `registration_number` — an organization's `business_registration` (matrícula, HRB…)
 *
 *   These three IDENTIFIER facts are never judged by the model (AlexAmin/marcus#1061): it extracts
 *   each mention's identifiers typed with `ENTITY_ID_TYPES`, and document-processing's code compares
 *   them — see `MERGE_EVIDENCE_ID_TYPE_FACTS`.
 * - `registry_number`, `cadastral_number` — real-property (`property`) registry (matrícula CNR /
 *   folio real) and cadastral numbers (`SV_PROPERTY_FACTS`)
 * - `plate`, `vin`, `engine_number` — motor-vehicle (`vehicle`) license plate / póliza, chassis /
 *   VIN, and engine serial number (`SV_VEHICLE_FACTS`)
 * - `address`, `birth_date`, `municipality`, `nationality`, `occupation`, `company` — the
 *   `PARTY_ATTRIBUTE_KEYS` of the same name (`municipality` is the SV domicilio, not a street address)
 * - `representative` — the same named legal representative / apoderado of an organization
 * - `contact` — the same email or phone
 * - `explicit_statement` — a document itself says the two are (or are not) the same
 * - `legal_form` — `entities.legal_form` (S.A. de C.V., GmbH…)
 * - `jurisdiction` — an organization's place of incorporation
 *
 * **The name is deliberately not a fact**: it is what paired the two mentions in the first place.
 * `marital_status`, `sex`, `place_of_birth` and age are left out on purpose — weak, or they change
 * between documents. Which facts are STRONG enough to auto-merge on is web's policy, not vocabulary
 * (`shared/entities/mergeEvidence.ts`).
 */
export const MERGE_EVIDENCE_FACTS = [
  'tax_id', 'personal_id', 'registration_number', 'registry_number', 'cadastral_number',
  'plate', 'vin', 'engine_number', 'address', 'birth_date', 'representative',
  'contact', 'explicit_statement', 'municipality', 'nationality', 'occupation', 'company',
  'legal_form', 'jurisdiction',
] as const
export type MergeEvidenceFact = (typeof MERGE_EVIDENCE_FACTS)[number]

/** Whether the two mentions agree on a fact (`match`) or state different values of the SAME kind
 *  (`conflict` — two different DUIs, never a DUI against a passport number). */
export const MERGE_EVIDENCE_RELATIONS = ['match', 'conflict'] as const
export type MergeEvidenceRelation = (typeof MERGE_EVIDENCE_RELATIONS)[number]

/** Bounds both services apply to one verdict's evidence: at most `maxItems` items, at most
 *  `maxValuesPerItem` verbatim values each, and each value cut to `maxValueChars` (ellipsis
 *  included). Values are untrusted document text. */
export const MERGE_EVIDENCE_LIMITS = { maxItems: 8, maxValuesPerItem: 2, maxValueChars: 120 } as const

/**
 * Which evidence fact an identifier of each `ENTITY_ID_TYPES` type is shown as (AlexAmin/marcus#1061).
 *
 * The merge judge only EXTRACTS identifiers, labelled with the specific type; code compares them
 * (`services/document-processing/src/pipeline/entityMergeIdentifiers.ts`) and only ever within one
 * specific type — a DUI (`national_id`) against a passport number is nothing, although both are shown
 * as `personal_id`. The display fact stays coarse on purpose: labels are country-neutral in this app
 * (`idTypeLabelKey`), and "Mismo NIT" for `tax_id` is a user decision.
 *
 * `other` maps to null: an identifier nobody could classify is never compared, since two "other"
 * identifiers may be two different kinds of document.
 */
export const MERGE_EVIDENCE_ID_TYPE_FACTS = {
  national_id: 'personal_id',
  passport: 'personal_id',
  foreigner_id: 'personal_id',
  tax_id: 'tax_id',
  business_registration: 'registration_number',
  other: null,
} as const satisfies Record<EntityIdType, MergeEvidenceFact | null>

/** At most this many identifiers are read per mention: a party rarely states more than a national
 *  id, a passport and a tax id, and each one costs a comparison. */
export const MERGE_JUDGE_MAX_IDENTIFIERS_PER_MENTION = 4
